import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Tabs, Tab, Spinner, Alert, Table, Button, Form } from 'react-bootstrap';
import tenderAPI from '../../../services/tenderAPI';
import { parivartanAPI, platformAPI } from '../../../services/parivartanAPI';
import MasterLookup from '../../../components/workspace/MasterLookup';
import { canEditTender } from '../../../utils/tenderRecordAccess';
import { useWorkspaceAccess } from '../../../hooks/useWorkspaceAccess';
import TenderAiAssistantTab from './TenderAiAssistantTab';
import TenderDocumentsTab from './TenderDocumentsTab';
import TenderCommercialsTab from './TenderCommercialsTab';
import TenderSubmissionTab from './TenderSubmissionTab';
import WorkPackagesPanel from './WorkPackagesPanel';
import FinanceCasePanel from './FinanceCasePanel';
import TenderEligibilityDesk from './TenderEligibilityDesk';
import { SecuritiesPanel, PartnersPanel, QueriesPanel, CorrigendaPanel, ScorePanel } from './PursuitPanels';
import TeamResourcesPanel from './TeamResourcesPanel';
import TenderTechnicalDesk from './TenderTechnicalDesk';
import { useAltF1TestFill } from '../../../hooks/useAltF1TestFill';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import PageHelp from '../../../components/workspace/PageHelp';
import { dummyParivartanPersonnelAllocationForm } from '../../../utils/testFormDummies';
import { downloadBidPackPdf } from '../../../utils/bidPackPdf';
import { loadOrganisationSettings } from '../../../utils/organisationSettings';
import { formatWorkspaceDate } from '../../../utils/workspaceDates';
import './TenderDetailWorkspace.scss';

const pinLabel = (pinned, version) => (pinned ? `v${version || '—'}` : 'Not pinned');

const credState = (link) => {
  const expiry = link.expiryStatus && link.expiryStatus !== 'unknown'
    ? link.expiryStatus.replace(/_/g, ' ')
    : '';
  if (!link.versionPinned) return { tone: 'stop', label: 'Needs pin' };
  if (link.versionStale) return { tone: 'wait', label: 'Library newer' };
  if (/expired/.test(expiry)) return { tone: 'stop', label: `Pinned · ${expiry}` };
  if (/expir|soon/.test(expiry)) return { tone: 'wait', label: `Pinned · ${expiry}` };
  return { tone: 'good', label: expiry ? `Pinned · ${expiry}` : 'Pinned' };
};

const personState = (item) => {
  if (!item.versionPinned) return { tone: 'stop', label: 'Needs pin' };
  if (item.versionStale) return { tone: 'wait', label: 'Roster newer' };
  return { tone: 'good', label: 'Pinned' };
};

const REASON_STEPS = new Set(['not_eligible', 'no_go', 'lost', 'not_qualified', 'cancelled', 'withdrawn']);
const RETURN_STAGES = [
  ['identified', 'Identified'],
  ['eligibility', 'Eligibility'],
  ['bid_decision', 'Bid decision'],
  ['preparing', 'In preparation'],
  ['ready_to_submit', 'Ready to submit'],
  ['submitted', 'Submitted'],
  ['under_evaluation', 'Under evaluation'],
  ['result_declared', 'Result declared']
];

function LifecyclePanel({ tender, onChanged }) {
  const life = tender.lifecycle || {};
  const steps = life.allowed || [];
  const [to, setTo] = useState(steps[0]?.to || '');
  const [reason, setReason] = useState('');
  const [returnTo, setReturnTo] = useState(life.journey || 'identified');
  const [outcomeReasonCode, setOutcomeReasonCode] = useState('');
  const [outcomeRemarks, setOutcomeRemarks] = useState('');
  const [reasons, setReasons] = useState([]);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const needsCatalog = REASON_STEPS.has(to);

  useEffect(() => {
    setTo(steps[0]?.to || '');
  }, [tender.workflowStage, tender.closingOutcome, life.ambiguous]);

  useEffect(() => {
    if (!needsCatalog) {
      setReasons([]);
      return undefined;
    }
    let stopped = false;
    platformAPI.listReference('outcome-reasons', { outcome: to })
      .then((response) => {
        if (!stopped) setReasons(response.data?.data?.values || []);
      })
      .catch(() => {
        if (!stopped) setReasons([]);
      });
    return () => { stopped = true; };
  }, [needsCatalog, to]);

  const move = async () => {
    setBusy(true);
    setMessage('');
    try {
      const response = await platformAPI.transitionTender(tender._id || tender.id, {
        to,
        reason,
        returnTo: to === 'reopen' ? returnTo : undefined,
        confirmReview: Boolean(life.ambiguous),
        outcomeReasonCode: needsCatalog ? outcomeReasonCode : undefined,
        outcomeRemarks: needsCatalog ? outcomeRemarks : undefined
      });
      if (response?.success === false) {
        setMessage(response.message || 'This tender could not be moved.');
        return;
      }
      setReason('');
      setOutcomeRemarks('');
      if (onChanged) await onChanged();
    } catch (error) {
      setMessage(error?.response?.data?.message || error.message || 'This tender could not be moved.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Alert variant="light" className="small mt-2">
      <div>{life.outcomeLabel ? `Outcome: ${life.outcomeLabel}` : `Journey: ${life.positionLabel || 'Not mapped'}`}</div>
      {life.reviewNote ? <div className="mt-1">{life.reviewNote}</div> : null}
      {tender.participationMode ? <div className="mt-1">Participation: {tender.participationMode === 'jv_lead' ? 'JV lead' : tender.participationMode === 'solo' ? 'Solo' : tender.participationMode === 'undecided' ? 'Undecided' : 'Not recorded'}</div> : null}
      {steps.length ? (
        <div className="d-flex flex-wrap gap-2 align-items-end mt-2">
          <Form.Group>
            <Form.Label>Next</Form.Label>
            <Form.Select value={to} onChange={(event) => setTo(event.target.value)}>
              {steps.map((step) => <option key={step.to} value={step.to}>{step.label}</option>)}
            </Form.Select>
          </Form.Group>
          {to === 'reopen' ? (
            <Form.Group>
              <Form.Label>Return to</Form.Label>
              <Form.Select value={returnTo} onChange={(event) => setReturnTo(event.target.value)}>
                {RETURN_STAGES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
              </Form.Select>
            </Form.Group>
          ) : null}
          <Form.Group>
            <Form.Label>{needsCatalog ? 'Reason' : 'Note'}</Form.Label>
            {needsCatalog ? (
              <Form.Select value={outcomeReasonCode} onChange={(event) => setOutcomeReasonCode(event.target.value)}>
                <option value="">Choose a reason</option>
                {reasons.filter((row) => row.status === 'active' || row.code === outcomeReasonCode).map((row) => (
                  <option key={row.code} value={row.code}>{row.label}</option>
                ))}
              </Form.Select>
            ) : (
              <Form.Control value={reason} onChange={(event) => setReason(event.target.value)} />
            )}
          </Form.Group>
          {needsCatalog ? (
            <Form.Group>
              <Form.Label>Remarks</Form.Label>
              <Form.Control value={outcomeRemarks} onChange={(event) => setOutcomeRemarks(event.target.value)} />
            </Form.Group>
          ) : null}
          <Button size="sm" disabled={busy || !to} onClick={move}>{busy ? 'Saving' : 'Apply'}</Button>
        </div>
      ) : null}
      {message ? <div className="mt-2">{message}</div> : null}
      <div className="mt-2 text-muted">
        Not in this release: {(life.laterPhase || []).map((item) => item.name).join(', ') || 'later-phase gates'}.
        Submission is recorded on the Submission tab after the tender is ready to submit, with a portal reference and acknowledgement.
      </div>
    </Alert>
  );
}

const TenderDetailWorkspace = () => {
  const { tenderId } = useParams();
  const location = useLocation();
  const { allowed, ready } = useWorkspaceAccess();
  const canReadCosts = ready && allowed(['tender.costing.read']);
  const canAssignTeam = ready && allowed(['tender.assign']);
  const canReadReports = ready && allowed(['tender.reports.read']);
  const [decisionRevision, setDecisionRevision] = useState(0);
  const [tab, setTab] = useState(location.state?.activeTab || 'overview');
  const [loading, setLoading] = useState(true);
  const [tender, setTender] = useState(null);
  const [estimates, setEstimates] = useState([]);
  const [error, setError] = useState('');
  const [effort, setEffort] = useState(null);
  const [award, setAward] = useState(null);
  const [personnel, setPersonnel] = useState([]);
  const [allocForm, setAllocForm] = useState({ personnelId: '', role: '' });
  const [credLinks, setCredLinks] = useState([]);
  const [library, setLibrary] = useState([]);
  const [pickCred, setPickCred] = useState('');
  const [peopleLinks, setPeopleLinks] = useState([]);
  const [roster, setRoster] = useState([]);
  const [pickPerson, setPickPerson] = useState('');

  useDocumentTitle(tender?.reference && tender?.title ? `${tender.reference} — ${tender.title}` : 'Tender');

  const reload = () => Promise.all([
    tenderAPI.getTenderById(tenderId),
    canReadCosts
      ? parivartanAPI.listCostEstimates(tenderId).catch(() => ({ data: { data: { estimates: [] } } }))
      : Promise.resolve({ data: { data: { estimates: [] } } }),
    canReadReports
      ? parivartanAPI.reportEffortVariance({ tenderId }).catch(() => ({ data: { data: null } }))
      : Promise.resolve({ data: { data: null } }),
    parivartanAPI.getAward(tenderId).catch(() => ({ data: { data: {} } })),
    parivartanAPI.listPersonnel({ limit: 100 }).catch(() => ({ data: { data: { personnel: [] } } })),
    parivartanAPI.listTenderCredentials(tenderId).catch(() => ({ data: { data: { links: [] } } })),
    parivartanAPI.listKnowledge().catch(() => ({ data: { data: { credentials: [] } } })),
    parivartanAPI.listPersonnelProposals(tenderId).catch(() => ({ data: { data: { proposals: [] } } }))
  ]).then(([tRes, eRes, evRes, aRes, pRes, cRes, kRes, peopleRes]) => {
    setTender(tRes?.data?.tender || tRes?.data?.data?.tender || null);
    setEstimates(canReadCosts ? (eRes.data?.data?.estimates || []) : []);
    const effortReport = canReadReports ? evRes.data?.data : null;
    if (effortReport && !canReadCosts) {
      delete effortReport.missingRates;
      (effortReport.rows || []).forEach((row) => {
        delete row.approvedCostPaise;
        delete row.baselineCostPaise;
        delete row.costVariancePaise;
        delete row.costLabel;
      });
    }
    setEffort(effortReport);
    const assignment = aRes.data?.data?.assignment || null;
    if (assignment && !canReadCosts) {
      delete assignment.baselineCostPaise;
      delete assignment.approvedEstimateId;
    }
    setAward(assignment);
    setPersonnel(pRes.data?.data?.personnel || []);
    setCredLinks(cRes.data?.data?.links || []);
    setLibrary(kRes.data?.data?.credentials || []);
    setPeopleLinks(peopleRes.data?.data?.proposals || []);
    setRoster(pRes.data?.data?.personnel || []);
  });

  useEffect(() => {
    if (!ready) return undefined;
    let active = true;
    reload()
      .catch(() => { if (active) setError('Unable to load tender workspace'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [tenderId, ready, canReadCosts, canReadReports]);

  useAltF1TestFill(Boolean(award), () => {
    setAllocForm(dummyParivartanPersonnelAllocationForm(personnel));
  });

  const approvedEstimate = estimates.find((e) => e.status === 'approved') || estimates.sort((a, b) => b.version - a.version)[0];

  const recordAward = async () => {
    await parivartanAPI.createAward(tenderId, {
      personnelAllocations: [],
      startDate: new Date().toISOString().slice(0, 10)
    });
    const a = await parivartanAPI.getAward(tenderId);
    setAward(a.data?.data?.assignment || null);
    reload();
  };

  const savePersonnelAllocation = async () => {
    if (!award?._id || !allocForm.personnelId) return;
    const next = [
      ...(award.personnelAllocations || []),
      { personnelId: allocForm.personnelId, role: allocForm.role || 'Consultant' }
    ];
    const r = await parivartanAPI.updateAssignment(award._id, { personnelAllocations: next });
    setAward(r.data?.data?.assignment || null);
    setAllocForm({ personnelId: '', role: '' });
  };

  const attachPerson = async () => {
    if (!pickPerson) return;
    const person = roster.find((item) => String(item._id) === String(pickPerson));
    await parivartanAPI.attachPersonnelToTenders({
      personnelId: pickPerson,
      tenderIds: [tenderId],
      proposedRole: person?.designation || undefined,
      pinnedCvVersion: person?.cvVersion || '1.0'
    });
    setPickPerson('');
    const next = await parivartanAPI.listPersonnelProposals(tenderId);
    setPeopleLinks(next.data?.data?.proposals || []);
  };

  const attachCredential = async () => {
    if (!pickCred) return;
    const credential = library.find((item) => String(item._id) === String(pickCred));
    await parivartanAPI.attachTenderCredentials({
      credentialId: pickCred,
      tenderIds: [tenderId],
      pinnedVersion: credential?.version || '1.0'
    });
    setPickCred('');
    const next = await parivartanAPI.listTenderCredentials(tenderId);
    setCredLinks(next.data?.data?.links || []);
  };

  const pinCredential = async (linkId) => {
    await parivartanAPI.pinTenderCredential(linkId, {});
    const next = await parivartanAPI.listTenderCredentials(tenderId);
    setCredLinks(next.data?.data?.links || []);
  };

  const detachCredential = async (linkId) => {
    await parivartanAPI.detachTenderCredential(linkId);
    const next = await parivartanAPI.listTenderCredentials(tenderId);
    setCredLinks(next.data?.data?.links || []);
  };

  const pinPersonCv = async (proposalId) => {
    await parivartanAPI.pinPersonnelProposal(proposalId, {});
    const next = await parivartanAPI.listPersonnelProposals(tenderId);
    setPeopleLinks(next.data?.data?.proposals || []);
  };

  const detachPerson = async (proposalId) => {
    await parivartanAPI.detachPersonnelProposal(proposalId);
    const next = await parivartanAPI.listPersonnelProposals(tenderId);
    setPeopleLinks(next.data?.data?.proposals || []);
  };

  const exportBidPack = async () => {
    const settings = await loadOrganisationSettings().catch(() => null);
    const workspace = settings?.settings?.workspace || {};
    const words = (value) => String(value || '')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (ch) => ch.toUpperCase());
    const clip = (value) => {
      const text = String(value || '').replace(/\s+/g, ' ').trim();
      if (text.length <= 280) return text;
      return `${text.slice(0, 280).replace(/\s+\S*$/, '')}...`;
    };
    const letterhead = String(workspace.letterhead || '').split('\n').map((line) => line.trim()).find(Boolean);
    downloadBidPackPdf({
      fileName: `${tender.reference || 'tender'}-bid-pack.pdf`,
      firm: workspace.displayName || 'Dhruv Consultancy',
      legalName: letterhead || workspace.legalName || workspace.displayName || 'Dhruv Consultancy',
      reference: tender.reference,
      title: tender.title,
      client: tender.organization,
      service: tender.serviceType || tender.sector || '',
      generated: formatWorkspaceDate(new Date()),
      description: clip(tender.description),
      facts: [
        { label: 'Stage', value: tender.lifecycle?.positionLabel || words(tender.workflowStage || tender.pipelineStage || tender.status) },
        { label: 'Deadline', value: tender.deadline ? formatWorkspaceDate(tender.deadline) : '' },
        { label: 'Location', value: tender.location || '' },
        { label: 'Type', value: tender.tenderType || '' }
      ].filter((item) => item.value),
      credentials: credLinks.map((link) => ({
        name: link.credentialTitle || 'Credential',
        detail: link.category || '',
        pinned: pinLabel(link.versionPinned, link.pinnedVersion),
        library: `v${link.libraryVersion || link.version || '—'}`,
        status: credState(link).label,
        tone: credState(link).tone
      })),
      people: peopleLinks.map((item) => ({
        name: item.personName || 'Person',
        detail: item.proposedRole || '',
        pinned: pinLabel(item.versionPinned, item.pinnedCvVersion),
        library: `v${item.libraryCvVersion || item.cvVersion || '—'}`,
        status: personState(item).label,
        tone: personState(item).tone
      }))
    });
  };

  if (loading) return <Spinner animation="border" size="sm" />;
  if (error || !tender) return <Alert variant="warning">{error || 'Tender not found'}</Alert>;

  const editable = canEditTender(tender);

  return (
    <div>
      <header className="bid-mast">
        <div>
          <p className="bid-mast__ref">{tender.reference}</p>
          <h1>{tender.title}</h1>
          <p>{[tender.organization, tender.serviceType || tender.sector].filter(Boolean).join(' · ')}</p>
        </div>
        <div className="bid-mast__actions">
          <PageHelp topic="pursuit" />
          <Button size="sm" variant="outline-primary" onClick={exportBidPack}>Export PDF</Button>
        </div>
      </header>
      <section className="bid-board">
        <header className="bid-board__head">
          <div>
            <h2>Credentials on this tender</h2>
            <p>Attach pins the library version on this bid. Re-pin if the library later moves ahead.</p>
          </div>
          <span className="bid-board__count">{String(credLinks.length).padStart(2, '0')}</span>
        </header>
        {credLinks.length ? credLinks.map((link) => {
          const state = credState(link);
          return (
            <article key={link._id} className="bid-row">
              <div>
                <strong>{link.credentialTitle}</strong>
                {link.category ? <small>{link.category}</small> : null}
              </div>
              <div className="bid-row__vers">
                <span>Pinned <b>{pinLabel(link.versionPinned, link.pinnedVersion)}</b></span>
                <span>Library <b>v{link.libraryVersion || link.version || '—'}</b></span>
              </div>
              <em className={`bid-state bid-state--${state.tone}`}>{state.label}</em>
              {editable ? (
                <div className="bid-row__acts">
                  {(!link.versionPinned || link.versionStale) ? (
                    <button type="button" onClick={() => pinCredential(link._id)}>Pin current</button>
                  ) : null}
                  <button type="button" className="is-remove" onClick={() => detachCredential(link._id)}>Remove</button>
                </div>
              ) : <span />}
            </article>
          );
        }) : (
          <p className="bid-empty">No library credential is attached to this pursuit yet.</p>
        )}
        {editable ? (
          <div className="bid-add">
            <MasterLookup
              options={library
                .filter((item) => !credLinks.some((link) => String(link.credentialId) === String(item._id)))
                .map((item) => ({
                  value: item._id,
                  label: item.title,
                  hint: `${item.category || 'Credential'}${item.version ? ` · v${item.version}` : ''}`,
                  aliases: [item.title, item.category, item.issuer, item.version].filter(Boolean)
                }))}
              value={pickCred}
              onChange={setPickCred}
              placeholder="Search the credential library"
            />
            <Button size="sm" onClick={attachCredential} disabled={!pickCred}>Attach & pin</Button>
          </div>
        ) : null}
      </section>
      <section className="bid-board">
        <header className="bid-board__head">
          <div>
            <h2>People named on this tender</h2>
            <p>Naming someone pins their CV version for this bid. Re-pin if the roster is updated.</p>
          </div>
          <span className="bid-board__count">{String(peopleLinks.length).padStart(2, '0')}</span>
        </header>
        {peopleLinks.length ? peopleLinks.map((item) => {
          const state = personState(item);
          return (
            <article key={item._id} className="bid-row">
              <div>
                <strong>{item.personName}</strong>
                {item.proposedRole ? <small>{item.proposedRole}</small> : null}
              </div>
              <div className="bid-row__vers">
                <span>Pinned CV <b>{pinLabel(item.versionPinned, item.pinnedCvVersion)}</b></span>
                <span>Roster CV <b>v{item.libraryCvVersion || item.cvVersion || '—'}</b></span>
              </div>
              <em className={`bid-state bid-state--${state.tone}`}>{state.label}</em>
              {editable ? (
                <div className="bid-row__acts">
                  {(!item.versionPinned || item.versionStale) ? (
                    <button type="button" onClick={() => pinPersonCv(item._id)}>Pin current</button>
                  ) : null}
                  <button type="button" className="is-remove" onClick={() => detachPerson(item._id)}>Remove</button>
                </div>
              ) : <span />}
            </article>
          );
        }) : (
          <p className="bid-empty">No one from the roster is named on this tender yet.</p>
        )}
        {editable ? (
          <div className="bid-add">
            <MasterLookup
              options={roster
                .filter((item) => item.isActive && !peopleLinks.some((link) => String(link.personnelId) === String(item._id)))
                .map((item) => ({
                  value: item._id,
                  label: item.name,
                  hint: `${item.designation || 'Role'}${item.cvVersion ? ` · CV v${item.cvVersion}` : ''}`,
                  aliases: [item.name, item.designation, item.cvVersion].filter(Boolean)
                }))}
              value={pickPerson}
              onChange={setPickPerson}
              placeholder="Search the roster"
            />
            <Button size="sm" onClick={attachPerson} disabled={!pickPerson}>Name & pin CV</Button>
          </div>
        ) : null}
      </section>
      {location.state?.success ? <Alert variant={location.state?.uploadFailures?.length ? 'warning' : 'success'}>{location.state.success}</Alert> : null}
      <Tabs activeKey={tab} onSelect={setTab} className="mb-3">
        <Tab eventKey="overview" title="Overview">
          <p>{tender.description}</p>
          <div className="small text-muted">Stage: {tender.lifecycle?.positionLabel || tender.workflowStage || tender.pipelineStage || tender.status}</div>
          <LifecyclePanel tender={tender} onChanged={reload} />
          <WorkPackagesPanel tenderId={tenderId} />
          {effort ? (
            <Alert variant="light" className="small mt-2">
              {canReadCosts ? 'Effort hours (cost is an internal estimate, not project profit): ' : 'Effort hours: '}
              planned {effort.plannedHours}h · pending {effort.pendingHours}h (excluded from actual) · approved {effort.approvedHours}h
              {canReadCosts && effort.missingRates ? ' · Missing rates on some lines' : ''}
            </Alert>
          ) : null}
        </Tab>
        <Tab eventKey="eligibility" title="Eligibility & Go / No-Go">
          <TenderEligibilityDesk tenderId={tenderId} tender={tender} onChanged={reload} refreshKey={decisionRevision} />
          <hr />
          <h3 className="h6">Weighted score</h3>
          <ScorePanel tenderId={tenderId} onChanged={() => setDecisionRevision((value) => value + 1)} />
        </Tab>
        <Tab eventKey="team" title="Team / Resources">
          <TeamResourcesPanel tenderId={tenderId} canEdit={canAssignTeam} />
        </Tab>
        <Tab eventKey="securities" title="BG / EMD">
          <FinanceCasePanel tenderId={tenderId} />
          <SecuritiesPanel tenderId={tenderId} />
        </Tab>
        <Tab eventKey="partners" title="JV / partners">
          <PartnersPanel tenderId={tenderId} />
        </Tab>
        <Tab eventKey="prebid" title="Pre-bid queries">
          <QueriesPanel tenderId={tenderId} />
        </Tab>
        <Tab eventKey="corrigenda" title="Corrigenda">
          <CorrigendaPanel tenderId={tenderId} />
        </Tab>
        <Tab eventKey="technical" title="Technical response">
          <TenderTechnicalDesk tenderId={tenderId} tender={tender} onChanged={reload} />
        </Tab>
        {canReadCosts ? (
          <Tab eventKey="commercials" title="Commercials">
            <TenderCommercialsTab tenderId={tenderId} />
          </Tab>
        ) : tab === 'commercials' ? (
          <Tab eventKey="commercials" title="Commercials">
            <Alert variant="warning">This area is restricted. Commercial estimates, rates, and worked examples are hidden.</Alert>
          </Tab>
        ) : null}
        <Tab eventKey="submission" title="Submission">
          <TenderSubmissionTab tenderId={tenderId} tender={tender} onRecorded={reload} />
        </Tab>
        <Tab eventKey="documents" title="Documents">
          <TenderDocumentsTab tenderId={tenderId} uploadFailures={location.state?.uploadFailures || []} uploadNotices={location.state?.uploadNotices || []} />
        </Tab>
        <Tab eventKey="award" title="Award & assignment">
          {award ? (
            <Alert variant="success" className="small">
              Awarded assignment active
              {canReadCosts ? ` · estimate baseline v${award.approvedEstimateVersion || '—'}` : ''}
              {canReadCosts && approvedEstimate ? ` · ${(approvedEstimate.totalCostPaise / 100).toLocaleString('en-IN')} INR planned cost` : ''}
            </Alert>
          ) : (
            <Button size="sm" className="mb-2" onClick={recordAward}>Record award & create assignment</Button>
          )}
          {award ? (
            <>
              <Table size="sm" className="mb-2">
                <thead><tr><th>Personnel</th><th>Role on assignment</th></tr></thead>
                <tbody>
                  {(award.personnelAllocations || []).map((p, i) => {
                    const person = personnel.find((x) => String(x._id) === String(p.personnelId));
                    return (
                      <tr key={i}>
                        <td>{person?.name || p.personnelId}</td>
                        <td>{p.role || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              <div className="row g-2 align-items-end">
                <div className="col-md-5">
                  <Form.Select
                    value={allocForm.personnelId}
                    onChange={(e) => setAllocForm({ ...allocForm, personnelId: e.target.value })}
                  >
                    <option value="">Select personnel</option>
                    {personnel.map((p) => (
                      <option key={p._id} value={p._id}>{p.name} — {p.designation}</option>
                    ))}
                  </Form.Select>
                </div>
                <div className="col-md-4">
                  <Form.Control
                    placeholder="Role on assignment"
                    value={allocForm.role}
                    onChange={(e) => setAllocForm({ ...allocForm, role: e.target.value })}
                  />
                </div>
                <div className="col-md-3">
                  <Button size="sm" onClick={savePersonnelAllocation}>Add allocation</Button>
                </div>
              </div>
            </>
          ) : null}
          {effort?.rows?.length ? (
            <div className="small mt-3">
              {effort.rows.map((row) => (
                <p key={`${row.stage}-${row.assignmentId || row.tenderId}`} className="mb-1">
                  {row.stage === 'assignment' ? 'Assignment' : 'Tender'} effort: approved {row.approvedHours || 0}h
                  {row.baselineHours != null ? ` against baseline ${row.baselineHours}h` : ' (baseline missing)'}
                  {row.hoursVariance != null ? ` · variance ${row.hoursVariance}h` : ''}
                  {canReadCosts && row.costIncomplete ? ' · cost incomplete' : ''}
                </p>
              ))}
              <p className="text-muted mb-0">Tender-stage hours and assignment-stage hours are kept separate. Cost is an internal effort estimate.</p>
            </div>
          ) : null}
        </Tab>
        <Tab eventKey="ai" title="Document Q&A">
          <TenderAiAssistantTab tenderId={tenderId} />
        </Tab>
      </Tabs>
    </div>
  );
};

export default TenderDetailWorkspace;
